import { useEffect, useRef, useState } from 'react';

const BARKY_URL = 'https://storage.readdy-site.link/project_files/c43d99a8-1e00-428b-96a3-32143e86ff11/d6c2f9b6-4541-4f92-a27c-4e3d3602e9a5_barky_sem_fundo.png?v=6c769196e5f19b9901d880a680aabc0f';

const items = [
  {
    title: 'Complete Rebranding',
    desc: 'New logo, visual identity and brand positioning built for luxury clients.',
  },
  {
    title: 'High-Conversion Website',
    desc: 'Your best salesperson working 24 hours a day, 7 days a week.',
  },
  {
    title: 'Corporate Social Media',
    desc: 'Elite content that positions you as the authority in your market.',
  },
  {
    title: 'Irresistible Offer Engineering',
    desc: 'Stop competing on price. Start selling value.',
  },
];

export default function ExtremeMakeoverSection() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="makeover"
      ref={ref}
      style={{ background: '#FFFFFF', padding: '80px 60px' }}
      className="px-4 md:px-[60px]"
    >
      <div className="max-w-7xl mx-auto">

        {/* Outer wrapper: Barky on far left + 2-col grid */}
        <div className="flex items-stretch gap-0">

          {/* Barky mascot — lateral esquerda dos cards, desktop only */}
          <div
            className={`hidden xl:flex flex-col items-center justify-end flex-shrink-0 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
            style={{ width: '220px', paddingRight: '20px', paddingBottom: '0' }}
          >
            {/* Speech bubble with tail pointing RIGHT toward cards */}
            <div
              className="rounded-xl px-4 py-3 text-center mb-4"
              style={{
                background: '#143C3C',
                border: '1px solid #64B4B4',
                color: '#64B4B4',
                fontSize: '11px',
                fontWeight: 700,
                letterSpacing: '0.02em',
                lineHeight: 1.5,
                position: 'relative',
                maxWidth: '190px',
              }}
            >
              Look at everything you can earn!
              {/* Bubble tail pointing RIGHT */}
              <span
                style={{
                  position: 'absolute',
                  right: '-8px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: 0,
                  height: 0,
                  borderTop: '8px solid transparent',
                  borderBottom: '8px solid transparent',
                  borderLeft: '8px solid #143C3C',
                }}
              />
            </div>
            {/* Barky PNG — transparent, no container, aligned bottom */}
            <img
              src={BARKY_URL}
              alt="Barky mascot"
              style={{ width: '220px', height: 'auto', objectFit: 'contain', display: 'block' }}
            />
          </div>

          {/* Barky mobile — above cards, centered, 140px */}
          <div className="flex xl:hidden flex-col items-center mb-6">
            <div
              className="rounded-xl px-3 py-2 text-center mb-3"
              style={{
                background: '#143C3C',
                border: '1px solid #64B4B4',
                color: '#64B4B4',
                fontSize: '11px',
                fontWeight: 700,
                lineHeight: 1.4,
              }}
            >
              Look at everything you can earn!
            </div>
            <img
              src={BARKY_URL}
              alt="Barky mascot"
              style={{ width: '140px', height: 'auto', objectFit: 'contain' }}
            />
          </div>

          {/* 2-column grid */}
          <div
            className={`flex-1 grid grid-cols-1 lg:grid-cols-2 gap-[60px] items-center transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
          >
            {/* LEFT — TEXT */}
            <div>
              {/* Eyebrow */}
              <p
                className="font-bold uppercase mb-4"
                style={{ color: '#DC5014', fontSize: '11px', letterSpacing: '0.25em' }}
              >
                THE PROBLEM
              </p>

              {/* Headline */}
              <h2
                className="font-bold mb-6 leading-tight font-playfair"
                style={{ fontSize: '36px', color: '#143C3C', lineHeight: 1.2 }}
              >
                You Lose High-Ticket Contracts
                <br />Not Because of Your Work —
                <br />But Because of Your Image.
              </h2>

              {/* Body */}
              <p
                className="mb-8"
                style={{ fontSize: '16px', color: '#555555', lineHeight: 1.8 }}
              >
                We transform your image from &apos;handyman&apos; into an authority ready for the $100,000 client. From branding to showroom, you start operating with the infrastructure of a real corporation — from day one as a Barkfy partner.
              </p>

              {/* 4 Items */}
              <ul className="flex flex-col gap-5">
                {items.map((item) => (
                  <li key={item.title} className="flex items-start gap-4">
                    <div
                      className="flex-shrink-0 flex items-center justify-center rounded-full"
                      style={{ width: '28px', height: '28px', background: '#DC5014', marginTop: '2px' }}
                    >
                      <i className="ri-check-line" style={{ color: '#FFFFFF', fontSize: '14px' }} />
                    </div>
                    <div>
                      <p className="font-bold" style={{ fontSize: '14px', color: '#143C3C', marginBottom: '3px' }}>
                        {item.title}
                      </p>
                      <p style={{ fontSize: '13px', color: '#777777', lineHeight: 1.6 }}>
                        {item.desc}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            {/* RIGHT — IMAGE */}
            <div className="order-first lg:order-last flex items-center justify-center">
              <div
                style={{
                  borderRadius: '8px',
                  overflow: 'hidden',
                  width: '100%',
                }}
              >
                <img
                  src="https://storage.readdy-site.link/project_files/c43d99a8-1e00-428b-96a3-32143e86ff11/4c579038-0741-48ad-bb2f-18d67cc96ca4_WhatsApp-Image-2026-02-26-at-9.59.40-AM.jpeg?v=1165491accf6af944503117eeb53e7b6"
                  alt="Barkfy brand transformation - contractor rebranding"
                  className="w-full object-cover object-center"
                  style={{ borderRadius: '8px', filter: 'none', opacity: 1, maxHeight: '480px', display: 'block' }}
                />
              </div>
            </div>
          </div>
          {/* end 2-col grid */}

        </div>
        {/* end outer wrapper */}

        {/* Closing line */}
        <div
          className={`flex flex-col items-center mt-16 transition-all duration-700 delay-300 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
        >
          <div style={{ width: '48px', height: '2px', background: '#DC5014', marginBottom: '20px' }} />
          <p
            className="text-center font-bold"
            style={{
              fontSize: '20px',
              color: '#143C3C',
              maxWidth: '720px',
              lineHeight: 1.6,
            }}
          >
            Level up your game: at Barkfy, your company integrates into an elite Business Hub, operating with the power and infrastructure of a $30M+ platform.
          </p>
        </div>

      </div>
    </section>
  );
}
